// GAVION self-service password reset: the two calls of the public function gavion-password-reset. No credential is
// ever sent (the function is public). The reset token only travels in a POST body, never in a URL or a log.

export const passwordResetUrl = (): string =>
  `${import.meta.env.VITE_SUPABASE_URL ?? ""}/functions/v1/gavion-password-reset`;

/** The token of an emailed link (#/activer?reset=…): 32 random bytes, base64url. */
export const RESET_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

type Fetch = typeof fetch;
const defaultFetch: Fetch = (input, init) => fetch(input, init);

/**
 * « Mot de passe oublié ? ». "sent" is the generic answer (the function says the same whether or not the address
 * has an account); "error" = the request did not reach it or it failed.
 */
export async function requestPasswordReset(
  email: string,
  lang: string,
  f: Fetch = defaultFetch,
): Promise<"sent" | "error"> {
  try {
    const res = await f(passwordResetUrl(), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, lang: lang === "en" ? "en" : "fr" }),
    });
    await res.body?.cancel().catch(() => undefined);
    return res.ok ? "sent" : "error";
  } catch {
    return "error";
  }
}

export type RedeemResult =
  | { ok: true; tokenHash: string }
  | { ok: false; reason: "invalid_link" | "error" };

/** The button of #/activer: spends the emailed token, returns the Supabase Auth recovery token hash to verify. */
export async function redeemPasswordReset(
  token: string,
  f: Fetch = defaultFetch,
): Promise<RedeemResult> {
  try {
    const res = await f(`${passwordResetUrl()}/redeem`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      token_hash?: unknown;
    };
    if (res.ok && typeof body.token_hash === "string" && body.token_hash)
      return { ok: true, tokenHash: body.token_hash };
    return {
      ok: false,
      reason:
        res.status === 410 || res.status === 400 ? "invalid_link" : "error",
    };
  } catch {
    return { ok: false, reason: "error" };
  }
}
